export interface DrawingPoint {
  readonly x: number;
  readonly y: number;
}

/** One finger/pen owns a stroke; secondary pointers cannot extend or finish it. */
export class DrawingPointerSession {
  private pointerId: number | null = null;
  private points: readonly DrawingPoint[] = [];

  begin(
    pointer: { readonly pointerId: number; readonly button: number; readonly isPrimary: boolean },
    point: DrawingPoint,
  ): readonly DrawingPoint[] | null {
    if (!pointer.isPrimary || pointer.button !== 0 || this.pointerId !== null) return null;
    this.pointerId = pointer.pointerId;
    this.points = [point];
    return this.points;
  }

  move(pointerId: number, point: DrawingPoint): readonly DrawingPoint[] | null {
    if (this.pointerId !== pointerId) return null;
    this.points = [...this.points, point];
    return this.points;
  }

  finish(pointerId: number, point: DrawingPoint): readonly DrawingPoint[] | null {
    const points = this.move(pointerId, point);
    if (points !== null) this.cancel(pointerId);
    return points;
  }

  cancel(pointerId?: number): boolean {
    if (pointerId !== undefined && pointerId !== this.pointerId) return false;
    this.pointerId = null;
    this.points = [];
    return true;
  }
}

/**
 * Scoped, non-passive Touch Events fallback for WebKit gesture arbitration.
 * Pointer Events still draw the stroke: these listeners only prevent native panning.
 * Never install this on document/body, where it would block unrelated controls.
 */
export function guardDrawingTouch(target: EventTarget): () => void {
  const preventPan = (event: Event) => {
    if (event.cancelable) event.preventDefault();
  };
  const options = { passive: false };
  target.addEventListener('touchstart', preventPan, options);
  target.addEventListener('touchmove', preventPan, options);
  return () => {
    target.removeEventListener('touchstart', preventPan);
    target.removeEventListener('touchmove', preventPan);
  };
}
